import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ContextMenuItem, Table } from 'redesign';
import { GetPartnerRelationsResponse } from 'hq/services/partner-service.types';
import { useHqRelativePath } from 'hq/hooks/useHqRelativePath';
import { hqRoutes } from 'hq/Routes';
import {
  PartnerRelationColumnDefinition,
  PartnerRelationTableOptions,
} from './partner-relations.types';
import { getPartnerRelationColumnDefinition } from './utils/get-partner-relations-column-definitions';
import { getPartnerRelationTableData } from './utils/get-partner-relations-table-data';
import { useArchivePartnerRelation } from '../hooks/use-archive-partner-relation';
import { useDialog } from '../../../../../ogp/providers/dialog-provider';
import { useAlert } from '../../../../../shared/components/alerts';

type Props = { data: GetPartnerRelationsResponse[]; partnerId: string; deactivated: boolean };

export const PartnerRelationsTable = ({ data, partnerId, deactivated }: Props) => {
  const { alertSuccess, alertError } = useAlert();
  const { openDialog, closeDialog } = useDialog();
  const navigate = useNavigate();
  const generatePath = useHqRelativePath();
  const { t } = useTranslation(['hq-screen-specific', 'web-common']);
  const columns = useMemo(() => getPartnerRelationColumnDefinition(t), [t]);
  const tableData = useMemo(() => getPartnerRelationTableData(data), [data]);
  const { mutate } = useArchivePartnerRelation();

  const handleArchival = (partnerRelationId: string) => {
    openDialog(
      { title: t('partners.relations.table.confirm_delete_title', { ns: 'hq-screen-specific' }) },
      () =>
        mutate(partnerRelationId, {
          onSettled: () => closeDialog(),
          onError: (e) => alertError(e),
          onSuccess: () =>
            alertSuccess(
              t('partners.relations.table.delete_success', { ns: 'hq-screen-specific' })
            ),
        })
    );
  };

  const getPartnerRelationTableActions = (partnerRelationId: string) => [
    <>
      <ContextMenuItem
        key="edit-relation"
        onClick={() =>
          navigate(generatePath(hqRoutes.PartnerRelationEdit, { id: partnerId, partnerRelationId }))
        }
        disabled={deactivated}
        data-testid="edit-partner-relation"
      >
        {t('partners.relations.table.actions.edit', { ns: 'hq-screen-specific' })}
      </ContextMenuItem>
      <ContextMenuItem
        onClick={() => handleArchival(partnerRelationId)}
        data-testid="archive-partner-relation"
      >
        {t('partners.relations.table.actions.delete', { ns: 'hq-screen-specific' })}
      </ContextMenuItem>
    </>,
  ];

  return (
    <Table<PartnerRelationColumnDefinition, PartnerRelationTableOptions>
      stickyHeader={true}
      columns={columns}
      data={tableData}
      data-testid="partner-relations-table"
      getRowActions={getPartnerRelationTableActions}
    />
  );
};
